import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  IconMouse,
  IconEye,
  IconRocket,
  IconShieldLock,
  IconAdjustments,
  IconBell,
  IconScale,
  IconEyeOff,
  IconShieldCheck,
  IconCheck,
  IconStar,
  IconKey,
  IconWorldPin,
  IconBrandGoogle,
} from '@tabler/icons-react';
import UploaderZone from '@components/UploaderZone';
import { USER_LIMITS } from '../config/limits';
import { ANNUAL_SEAT_PRICE_PER_MONTH, MONTHLY_SEAT_PRICE_PER_MONTH } from '../config/pricing';
import { formatBytes } from '@utils/formatBytes';
import { getCurrentSubdomain } from '@utils/subdomain';
import { isLoggedIn } from '@services/auth';
import { rememberPostLoginReturnUrl } from '@services/postLoginIntent';
import { BrandText } from '@components/BrandText';
import { BillingCadenceToggle } from '@components/BillingCadenceToggle';
import './HomePage.css';

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Redirect to upload page if on a company subdomain
  useEffect(() => {
    const subdomain = getCurrentSubdomain();
    if (subdomain) {
      if (isLoggedIn()) {
        navigate({ to: '/upload', replace: true });
      } else {
        const returnUrl = encodeURIComponent(window.location.origin + '/upload');
        navigate({ to: '/login', search: { returnUrl }, replace: true });
      }
    }
  }, [navigate]);
  const [uploaderHasFiles, setUploaderHasFiles] = useState(false);
  const [proBillingCadence, setProBillingCadence] = useState<'yearly' | 'monthly'>('yearly');
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());

  const proPriceYearly = ANNUAL_SEAT_PRICE_PER_MONTH;
  const proPriceMonthly = MONTHLY_SEAT_PRICE_PER_MONTH;

  const mbToBytes = (mb: number) => mb * 1024 * 1024;

  const formatMonthlyAmount = (mb: number) =>
    t('home.pricing.limit.perMonth', {
      amount: formatBytes(mbToBytes(mb), 0, t),
    });

  const featureKeys = useMemo(
    () => [
      'home.pricing.feature.encryptedBrowser',
      'home.pricing.feature.encryptedAtRest',
      'home.pricing.feature.zeroKnowledge',
      'home.pricing.feature.secureComm',
      'home.pricing.feature.passwordProtection',
      'home.pricing.feature.emailNotification',
      'home.pricing.feature.networkResiliency',
      'home.pricing.feature.dataResidency',
      'home.pricing.feature.dataRetention',
      'home.pricing.feature.domainLicensing',
      'home.pricing.feature.sso',
      'home.pricing.feature.branding',
      'home.pricing.feature.maxDownloadCount',
      'home.pricing.feature.linksManagement',
    ],
    []
  );

  const sharedFeatures = useMemo(
    () =>
      featureKeys.map((key) => ({
        id: key,
        label: t(key),
        description: t(`${key}.description`),
      })),
    [featureKeys, t]
  );

  const toggleFeature = (id: string) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const freeUploadCapacityLabel = formatMonthlyAmount(USER_LIMITS.freeAccount.uploadMB);
  const proUploadCapacityLabel = formatMonthlyAmount(USER_LIMITS.licensedAccount.uploadMB);

  const proPriceLabel =
    proBillingCadence === 'yearly'
      ? t('home.pricing.pro.priceYearly', { price: proPriceYearly })
      : t('home.pricing.pro.priceMonthly', { price: proPriceMonthly });

  return (
    <div className="page home-page">
      <section className={uploaderHasFiles ? 'home-hero home-hero--uploader-expanded' : 'home-hero'}>
        {!uploaderHasFiles && (
          <div className="home-hero-copy">
            <h1>{t('home.hero.title')}</h1>
            <p className="home-hero-subtitle">
              <BrandText text={t('home.hero.subtitle')} />
            </p>
            <ul className="home-hero-list">
              <li>
                <IconCheck size={18} className="home-hero-list-icon" aria-hidden="true" />
                <span>{t('home.hero.point1')}</span>
              </li>
              <li>
                <IconCheck size={18} className="home-hero-list-icon" aria-hidden="true" />
                <span>{t('home.hero.point2')}</span>
              </li>
              <li>
                <IconCheck size={18} className="home-hero-list-icon" aria-hidden="true" />
                <span>{t('home.hero.point3')}</span>
              </li>
            </ul>
          </div>
        )}
        <div className={uploaderHasFiles ? 'home-hero-uploader home-hero-uploader--has-files' : 'home-hero-uploader'}>
          <UploaderZone onHasFilesChange={setUploaderHasFiles} />
        </div>
      </section>

      <section className="home-section home-why" id="why">
        <div className="home-block">
          <h2 className="home-section-title">
            <BrandText text={t('home.why.title')} />
          </h2>
          <div className="home-why-grid">
            <div className="home-why-column home-why-column--left">
              <article className="home-card">
                <div className="home-card-header">
                  <IconMouse size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.simple.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.simple.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconEye size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.trust.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.trust.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconRocket size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.performance.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.performance.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconWorldPin size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.residency.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.residency.body')} />
                </p>
              </article>
            </div>
            <div className="home-why-column home-why-column--center">
              <article className="home-card">
                <div className="home-card-header">
                  <IconShieldLock size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.team.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.team.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconAdjustments size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.control.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.control.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconBell size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.emailNotification.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.emailNotification.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconKey size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.sso.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.sso.body')} />
                </p>
              </article>
            </div>
            <div className="home-why-column home-why-column--right">
              <article className="home-card">
                <div className="home-card-header">
                  <IconScale size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.compliance.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.compliance.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconEyeOff size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.audit.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.audit.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconShieldCheck size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.support.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.support.body')} />
                </p>
              </article>
              <article className="home-card">
                <div className="home-card-header">
                  <IconBrandGoogle size={22} className="home-card-icon" aria-hidden="true" />
                  <h3>
                    <BrandText text={t('home.why.branding.title')} />
                  </h3>
                </div>
                <p>
                  <BrandText text={t('home.why.branding.body')} />
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-pricing">
        <div className="home-block home-pricing-block">
          <div className="home-pricing-intro" id="pricing">
            <h2 className="home-section-title">
              <BrandText text={t('home.pricing.title')} />
            </h2>
            <p className="home-section-intro">
              <BrandText text={t('home.pricing.subtitle')} />
            </p>
          </div>
          <div className="home-pricing-tiers">
            <article
              className="home-pricing-tier home-pricing-tier--features"
              aria-label={t('home.pricing.features.title', 'Features')}
            >
              <div className="home-pricing-tier-header">
                <h3>{t('home.pricing.features.title')}</h3>
              </div>
              <dl className="home-pricing-limits">
                {sharedFeatures.map((feature) => {
                  const isExpanded = expandedFeatures.has(feature.id);
                  return (
                    <div className="home-pricing-limit home-pricing-feature" key={feature.id}>
                      <dt className="home-pricing-feature-label" aria-hidden="true" />
                      <dd className="home-pricing-feature-content">
                        <button
                          type="button"
                          className="home-pricing-feature-toggle"
                          onClick={() => toggleFeature(feature.id)}
                          aria-expanded={isExpanded}
                        >
                          <span className="home-pricing-feature-name">
                            <IconCheck
                              size={18}
                              className="home-pricing-feature-icon"
                              aria-hidden="true"
                            />
                            <span className="home-pricing-feature-name-text">{feature.label}</span>
                          </span>
                          <span className="home-pricing-feature-toggle-icon">{isExpanded ? '−' : '+'}</span>
                        </button>
                        <div
                          className={
                            isExpanded
                              ? 'home-pricing-feature-description is-open'
                              : 'home-pricing-feature-description'
                          }
                        >
                          <p>{feature.description}</p>
                        </div>
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </article>

            <article
              className="home-pricing-tier home-pricing-tier--highlight home-pricing-tier--subscriptions"
              aria-label={t('home.pricing.subscriptions.title', 'Plans')}
            >
              <div className="home-pricing-tier-header">
                <h3>{t('home.pricing.subscriptions.title')}</h3>
              </div>
              <dl className="home-pricing-limits">
                <div className="home-pricing-limit">
                  <dt>{t('home.pricing.free.title')}</dt>
                  <dd>{freeUploadCapacityLabel}</dd>
                </div>
                <div className="home-pricing-limit">
                  <dt>{t('home.pricing.pro.title')}</dt>
                  <dd>{proUploadCapacityLabel}</dd>
                </div>
              </dl>
              <p className="home-pricing-shared-note">{t('home.pricing.pro.sharedUploadNote')}</p>
              <div className="home-pricing-tier-footer">
                <div className="home-pricing-tier-price-row">
                  <p className="home-pricing-tier-price">
                    {t('home.pricing.pro.forOnlyPrefix', 'For only')}{' '}
                    {proPriceLabel}
                  </p>
                  <BillingCadenceToggle
                    value={proBillingCadence}
                    onChange={setProBillingCadence}
                    ariaLabel={t('subscription.billingCycle')}
                  />
                </div>

                <button
                  type="button"
                  className="home-pricing-subscribe"
                  onClick={() => {
                    if (isLoggedIn()) {
                      navigate({
                        to: '/dashboard/subscription',
                        search: { cadence: proBillingCadence },
                      });
                    } else {
                      const returnUrl = '/dashboard/subscription';
                      rememberPostLoginReturnUrl(`${returnUrl}?cadence=${proBillingCadence}`);
                      navigate({ to: '/signup', search: { returnUrl } });
                    }
                  }}
                >
                  {t('home.pricing.subscribeCta')}
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

    </div>
  );
}

export default HomePage;
